import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/Button";
import { FormCard } from "@/components/FormCard";
import { forgotPasswordSchema } from "@/model/authSchema";
import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { Link, useSearchParams, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { useAuth } from "@/hooks/useAuth";

type TForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>;

type ForgotPasswordProps = {
  handleSuccess?: () => void;
};

export default function ForgotPassword({ handleSuccess }: ForgotPasswordProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signInAndRedirect } = useAuth();
  const emailFromQuery = searchParams.get("email") || "";
  const redirectFromQuery = searchParams.get("redirect") || "";
  const [isResending, setIsResending] = useState(false);
  const [isPasswordPopoverOpen, setIsPasswordPopoverOpen] = useState(false);

  const form = useForm<TForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: emailFromQuery,
      verificationCode: "",
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    if (emailFromQuery) {
      form.setValue("email", emailFromQuery);
    }
  }, [emailFromQuery, form]);

  const handleResendCode = async () => {
    if (!emailFromQuery) return;

    setIsResending(true);
    try {
      await resetPassword({ username: emailFromQuery });
      toast.success("Verification code sent to your email");
    } catch (error: any) {
      const errorType = error.code || error.name || error.__type;
      if (errorType === "LimitExceededException") {
        toast.error("Too many requests. Please try again later.");
      } else {
        toast.error("Failed to resend code. Please try again.");
      }
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data: TForgotPasswordSchema) => {
    const { email, verificationCode, password } = data;

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: password,
      });

      toast.success("Password reset successfully");

      const signInResult = await signInAndRedirect(email, password);
      console.log("signInResult:", signInResult);
      if (signInResult.success) {
        navigate(redirectFromQuery ? `${redirectFromQuery}` : "/");
        handleSuccess?.();
      } else {
        toast.error("Password reset successful. Please sign in manually.");
        navigate("/login");
      }
    } catch (error: any) {
      const errorType = error.code || error.name || error.__type;
      if (errorType === "CodeMismatchException") {
        form.setError("verificationCode", {
          type: "manual",
          message: "Invalid verification code",
        });
      } else if (errorType === "ExpiredCodeException") {
        form.setError("verificationCode", {
          type: "manual",
          message: "Verification code has expired",
        });
      } else {
        form.setError("verificationCode", {
          type: "manual",
          message: "Something went wrong. Please try again.",
        });
      }
    }
  };

  return (
    <FormCard
      form={form}
      onSubmit={onSubmit}
      title="Forgot password"
      description="Enter the verification code and your new password"
      className="w-full"
    >
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center justify-between">
              <span>Email</span>
              <Button
                variant="link"
                className="hover:underline p-0 h-auto text-sm"
                type="button"
                disabled={isResending || !emailFromQuery}
                onClick={handleResendCode}
              >
                {isResending ? "Resending..." : "Resend Code"}
              </Button>
            </FormLabel>
            <FormControl>
              <Input placeholder="you@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="verificationCode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Verification Code</FormLabel>
            <FormControl>
              <Input placeholder="Enter verification code" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="password"
        render={({ field }) => (
          <FormItem className="relative">
            <FormLabel>New Password</FormLabel>
            <FormControl>
              <PasswordInput
                {...field}
                placeholder="********"
                onFocus={() => setIsPasswordPopoverOpen(true)}
                onBlur={() => setIsPasswordPopoverOpen(false)}
              />
            </FormControl>
            <FormMessage className="whitespace-pre-line" />
            {isPasswordPopoverOpen && (
              <PasswordRequirements password={form.watch("password")} />
            )}
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="confirmPassword"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Confirm New Password</FormLabel>
            <FormControl>
              <PasswordInput placeholder="********" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <Button disabled={form.formState.isSubmitting} type="submit">
        Reset Password
      </Button>

      <div className="text-center text-sm">
        <Button variant="link" className="hover:underline">
          <Link to="/login">Back to Sign In</Link>
        </Button>
      </div>
    </FormCard>
  );
}
